import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Calculator, 
  Users, 
  Shield, 
  Award,
  Clock,
  CheckCircle,
  ArrowRight,
  Phone,
  Server,
  Sparkles,
  FileText,
  Mail,
  Loader2,
  Download
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

interface CalculatorResult {
  minPrice: number;
  maxPrice: number;
  timeline: string;
  recommendedPackage: string;
  packageUrl: string;
  includes: string[];
  disclaimer: string;
}

const emailSchema = z.object({
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters")
    .refine((val) => {
      const disposableDomains = ['tempmail.com', 'throwaway.email', '10minutemail.com', 'guerrillamail.com', 'mailinator.com'];
      const domain = val.split('@')[1]?.toLowerCase();
      return !disposableDomains.includes(domain);
    }, "Please use a business email address"),
});

const CyberEssentialsCostCalculator = () => {
  const [employeeCount, setEmployeeCount] = useState<string>("");
  const [securityPosture, setSecurityPosture] = useState<string>("");
  const [certificationLevel, setCertificationLevel] = useState<string>("");
  const [itComplexity, setItComplexity] = useState<string>("");
  const [result, setResult] = useState<CalculatorResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  
  // Email capture state
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfSent, setPdfSent] = useState(false);
  
  const { toast } = useToast();

  const calculateCost = () => {
    if (!employeeCount || !securityPosture || !certificationLevel || !itComplexity) {
      return;
    }

    setIsCalculating(true);

    // Simulate calculation delay for effect
    setTimeout(() => {
      let minPrice = 0;
      let maxPrice = 0;
      let timeline = "";
      let recommendedPackage = "";
      let packageUrl = "";
      let includes: string[] = [];

      // Base pricing by employee count
      switch (employeeCount) {
        case "1-9":
          if (certificationLevel === "ce") {
            minPrice = 495;
            maxPrice = 1495;
            timeline = "1-3 weeks";
            recommendedPackage = "Cyber Essentials Micro Package";
            packageUrl = "/services/cyber-essentials-micro";
            includes = [
              "Gap analysis assessment",
              "Remediation guidance",
              "SAQ submission support",
              "IASME certification fee included",
              "Certificate delivery"
            ];
          } else {
            minPrice = 1995;
            maxPrice = 2995;
            timeline = "3-5 weeks";
            recommendedPackage = "Cyber Essentials Plus Micro";
            packageUrl = "/services/cyber-essentials-micro";
            includes = [
              "Everything in CE package",
              "Technical vulnerability scan",
              "Penetration testing",
              "On-site or remote verification",
              "Plus certification"
            ];
          }
          break;
        case "10-49":
          if (certificationLevel === "ce") {
            minPrice = 995;
            maxPrice = 1495;
            timeline = "3-5 weeks";
            recommendedPackage = "Cyber Essentials Standard";
            packageUrl = "/services/cyber-essentials-certification-uk";
            includes = [
              "Comprehensive gap analysis",
              "Policy template library",
              "Remediation support",
              "SAQ completion assistance",
              "Certification guidance"
            ];
          } else {
            minPrice = 2495;
            maxPrice = 3495;
            timeline = "4-6 weeks";
            recommendedPackage = "Cyber Essentials Plus Standard";
            packageUrl = "/services/cyber-essentials-certification-uk";
            includes = [
              "Everything in CE package",
              "External vulnerability assessment",
              "Internal security testing",
              "Technical verification",
              "Plus certification & badge"
            ];
          }
          break;
        case "50-249":
          if (certificationLevel === "ce") {
            minPrice = 1495;
            maxPrice = 2495;
            timeline = "4-6 weeks";
            recommendedPackage = "Cyber Essentials Business";
            packageUrl = "/services/cyber-essentials-certification-uk";
            includes = [
              "In-depth security assessment",
              "Multi-site coordination",
              "Comprehensive remediation plan",
              "Staff awareness support",
              "Priority certification"
            ];
          } else {
            minPrice = 3495;
            maxPrice = 4995;
            timeline = "6-8 weeks";
            recommendedPackage = "Cyber Essentials Plus Business";
            packageUrl = "/services/cyber-essentials-certification-uk";
            includes = [
              "Everything in CE package",
              "Multi-system testing",
              "Comprehensive penetration test",
              "Executive summary report",
              "Plus certification"
            ];
          }
          break;
        case "250+":
          minPrice = 0; // Custom quote
          maxPrice = 0;
          timeline = "6-10 weeks";
          recommendedPackage = "Enterprise Custom Package";
          packageUrl = "/contact";
          includes = [
            "Dedicated account manager",
            "Enterprise-wide assessment",
            "Custom remediation roadmap",
            "Ongoing compliance support",
            "Priority support channel"
          ];
          break;
      }

      // Security posture adjustments
      if (employeeCount !== "250+") {
        switch (securityPosture) {
          case "strong":
            // No adjustment - ready to certify
            break;
          case "moderate":
            minPrice += 200;
            maxPrice += 400;
            break;
          case "limited":
            minPrice += 400;
            maxPrice += 800;
            break;
          case "unknown":
            minPrice += 200;
            maxPrice += 600;
            break;
        }

        // IT complexity adjustments
        switch (itComplexity) {
          case "simple":
            // No adjustment for cloud-only
            break;
          case "standard":
            minPrice += 150;
            maxPrice += 300;
            break;
          case "complex":
            minPrice += 300;
            maxPrice += 500;
            break;
        }
      }

      setResult({
        minPrice,
        maxPrice,
        timeline,
        recommendedPackage,
        packageUrl,
        includes,
        disclaimer: "Estimates are indicative and subject to formal assessment. IASME certification fees may be charged separately for some packages."
      });
      setIsCalculating(false);
    }, 800);
  };

  const resetCalculator = () => {
    setEmployeeCount("");
    setSecurityPosture("");
    setCertificationLevel("");
    setItComplexity("");
    setResult(null);
    setPdfSent(false);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    try {
      emailSchema.parse({ email });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Invalid Email",
          description: error.errors[0].message,
          variant: "destructive",
        });
        return;
      }
    }

    if (!consent) {
      toast({
        title: "Consent Required",
        description: "Please agree to receive your PDF breakdown.",
        variant: "destructive",
      });
      return;
    }

    if (!result) return;

    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("send-calculator-pdf", {
        body: {
          email,
          employeeCount,
          securityPosture,
          certificationLevel,
          itComplexity,
          minPrice: result.minPrice,
          maxPrice: result.maxPrice,
          timeline: result.timeline,
          recommendedPackage: result.recommendedPackage,
          includes: result.includes,
        },
      });

      if (error) throw error;

      setPdfSent(true);
      setShowEmailCapture(false);
      toast({
        title: "PDF Sent!",
        description: "Check your email for your detailed cost breakdown.",
      });
    } catch (error: any) {
      console.error("Error sending PDF:", error);
      toast({
        title: "Error",
        description: "Failed to send PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormComplete = employeeCount && securityPosture && certificationLevel && itComplexity;

  return (
    <>
      <Card className="w-full max-w-2xl mx-auto border-2 border-primary/20 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-t-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Calculator className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-xl md:text-2xl">
                Cyber Essentials Cost Calculator
              </CardTitle>
              <p className="text-sm text-primary-foreground/80 mt-1">
                Get an instant estimate for your certification
              </p>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {!result ? (
            <>
              {/* Step 1: Employee Count */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  How many employees do you have?
                </Label>
                <RadioGroup
                  value={employeeCount}
                  onValueChange={setEmployeeCount}
                  className="grid grid-cols-2 gap-3"
                >
                  {[
                    { value: "1-9", label: "1-9", desc: "Micro business" },
                    { value: "10-49", label: "10-49", desc: "Small business" },
                    { value: "50-249", label: "50-249", desc: "Medium business" },
                    { value: "250+", label: "250+", desc: "Enterprise" }
                  ].map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem
                        value={option.value}
                        id={`emp-${option.value}`}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={`emp-${option.value}`}
                        className="flex flex-col items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                      >
                        <span className="font-bold text-lg">{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.desc}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Step 2: Security Posture */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  What's your current security posture?
                </Label>
                <Select value={securityPosture} onValueChange={setSecurityPosture}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select your security level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strong">
                      <span className="font-medium">Strong</span> - We have policies & controls in place
                    </SelectItem>
                    <SelectItem value="moderate">
                      <span className="font-medium">Moderate</span> - Some controls, but gaps exist
                    </SelectItem>
                    <SelectItem value="limited">
                      <span className="font-medium">Limited</span> - Minimal formal security measures
                    </SelectItem>
                    <SelectItem value="unknown">
                      <span className="font-medium">Not sure</span> - Need assessment
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Step 3: Certification Level */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Award className="h-4 w-4 text-primary" />
                  Which certification do you need?
                </Label>
                <RadioGroup
                  value={certificationLevel}
                  onValueChange={setCertificationLevel}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  <div>
                    <RadioGroupItem
                      value="ce"
                      id="cert-ce"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="cert-ce"
                      className="flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    >
                      <span className="font-bold">Cyber Essentials</span>
                      <span className="text-sm text-muted-foreground">Self-assessment verified by certifying body</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem
                      value="ceplus"
                      id="cert-ceplus"
                      className="peer sr-only"
                    />
                    <Label
                      htmlFor="cert-ceplus"
                      className="flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                    >
                      <span className="font-bold flex items-center gap-2">
                        Cyber Essentials Plus
                        <Badge variant="secondary" className="text-xs">Higher Assurance</Badge>
                      </span>
                      <span className="text-sm text-muted-foreground">Includes technical verification testing</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Step 4: IT Complexity */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Server className="h-4 w-4 text-primary" />
                  What's your IT setup like?
                </Label>
                <Select value={itComplexity} onValueChange={setItComplexity}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select your IT complexity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple">
                      <span className="font-medium">Simple</span> - Cloud-only (Microsoft 365, Google Workspace)
                    </SelectItem>
                    <SelectItem value="standard">
                      <span className="font-medium">Standard</span> - Mix of cloud and local systems
                    </SelectItem>
                    <SelectItem value="complex">
                      <span className="font-medium">Complex</span> - On-premise servers, multiple locations
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Calculate Button */}
              <Button
                onClick={calculateCost}
                disabled={!isFormComplete || isCalculating}
                className="w-full py-6 text-lg"
                size="lg"
              >
                {isCalculating ? (
                  <>
                    <Sparkles className="mr-2 h-5 w-5 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Calculator className="mr-2 h-5 w-5" />
                    Calculate My Cost
                  </>
                )}
              </Button>
            </>
          ) : (
            /* Results Display */
            <div className="space-y-6 animate-in fade-in-50 duration-500">
              {/* Price Display */}
              <div className="text-center p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl border border-primary/20">
                <p className="text-sm text-muted-foreground mb-2">Estimated Investment</p>
                {result.minPrice > 0 ? (
                  <div className="text-4xl md:text-5xl font-bold text-primary">
                    £{result.minPrice.toLocaleString()} - £{result.maxPrice.toLocaleString()}
                  </div>
                ) : (
                  <div className="text-3xl md:text-4xl font-bold text-primary">
                    Custom Quote Required
                  </div>
                )}
                <p className="text-sm text-muted-foreground mt-2">
                  + VAT where applicable
                </p>
              </div>

              {/* Package Info */}
              <div className="grid gap-4">
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Award className="h-5 w-5 text-primary" />
                    <span className="font-medium">Recommended Package</span>
                  </div>
                  <Badge variant="secondary">{result.recommendedPackage}</Badge>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-primary" />
                    <span className="font-medium">Estimated Timeline</span>
                  </div>
                  <Badge variant="outline">{result.timeline}</Badge>
                </div>
              </div>

              {/* What's Included */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  What's Included
                </h4>
                <ul className="space-y-2">
                  {result.includes.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* PDF Download CTA */}
              {!pdfSent ? (
                <div className="p-4 bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-accent/20 rounded-lg">
                      <FileText className="h-5 w-5 text-accent-foreground" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">Get Your Detailed PDF Breakdown</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Receive a comprehensive cost estimate with timeline, checklist, and next steps.
                      </p>
                      <Button
                        onClick={() => setShowEmailCapture(true)}
                        variant="secondary"
                        className="mt-3"
                        size="sm"
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Send Me the PDF
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">PDF Sent!</p>
                      <p className="text-sm text-green-600 dark:text-green-400">Check your inbox for the detailed breakdown.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <p className="text-xs text-muted-foreground italic">
                * {result.disclaimer}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="flex-1" size="lg">
                  <Link to={result.packageUrl}>
                    {result.minPrice > 0 ? "Get Started" : "Get Custom Quote"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="flex-1" size="lg">
                  <Link to="/contact">
                    <Phone className="mr-2 h-4 w-4" />
                    Book Consultation
                  </Link>
                </Button>
              </div>

              {/* Reset */}
              <Button
                variant="ghost"
                onClick={resetCalculator}
                className="w-full text-muted-foreground"
              >
                ← Calculate Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Capture Dialog */}
      <Dialog open={showEmailCapture} onOpenChange={setShowEmailCapture}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Get Your PDF Breakdown
            </DialogTitle>
            <DialogDescription>
              Enter your email to receive a detailed cost estimate with:
            </DialogDescription>
          </DialogHeader>
          
          <ul className="space-y-2 text-sm text-muted-foreground my-4">
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Complete pricing breakdown
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Certification timeline & milestones
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              The 5 Cyber Essentials controls explained
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Next steps to get certified
            </li>
          </ul>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <Label htmlFor="calc-email">Business Email *</Label>
              <Input
                id="calc-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="flex items-start space-x-2">
              <Checkbox
                id="calc-consent"
                checked={consent}
                onCheckedChange={(checked) => setConsent(checked === true)}
                disabled={isSubmitting}
              />
              <Label htmlFor="calc-consent" className="text-sm leading-relaxed cursor-pointer">
                I consent to receiving my PDF and occasional updates about Cyber Essentials certification. 
                See our <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>.
              </Label>
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting || !consent}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send My PDF Breakdown
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CyberEssentialsCostCalculator;
